import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/Roadmaps.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const Roadmaps = () => {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [viewMode, setViewMode] = useState("table");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const roadmapsData = [
    {
      id: 1,
      name: "Full Stack Development",
      description:
        "Complete web development roadmap covering frontend, backend, and deployment",
      students: 245,
      completionRate: 68,
      status: "Active",
      created: "2024-01-10",
      duration: "6 months",
      modules: 12,
      difficulty: "Intermediate",
      mentor: "Dr. Ali Ahmad",
      category: "Web Development",
    },
    {
      id: 2,
      name: "Data Science Path",
      description:
        "Master data analysis, machine learning, and AI fundamentals",
      students: 189,
      completionRate: 72,
      status: "Active",
      created: "2024-02-15",
      duration: "8 months",
      modules: 15,
      difficulty: "Advanced",
      mentor: "Prof. Ayesha Tariq",
      category: "Data Science",
    },
    {
      id: 3,
      name: "Mobile Development",
      description: "Build native and cross-platform mobile applications",
      students: 156,
      completionRate: 58,
      status: "Active",
      created: "2024-03-20",
      duration: "5 months",
      modules: 10,
      difficulty: "Intermediate",
      mentor: "Eng. Bilal Hassan",
      category: "Mobile Development",
    },
    {
      id: 4,
      name: "DevOps Engineering",
      description:
        "Learn CI/CD, containerization, cloud platforms, and automation",
      students: 134,
      completionRate: 45,
      status: "Draft",
      created: "2024-04-05",
      duration: "7 months",
      modules: 14,
      difficulty: "Advanced",
      mentor: "Mr. Kamran Siddiqui",
      category: "DevOps",
    },
    {
      id: 5,
      name: "UI/UX Design Mastery",
      description:
        "Design thinking, wireframing, prototyping, and user research",
      students: 198,
      completionRate: 75,
      status: "Active",
      created: "2024-01-25",
      duration: "4 months",
      modules: 8,
      difficulty: "Beginner",
      mentor: "Ms. Zainab Yusuf",
      category: "Design",
    },
    {
      id: 6,
      name: "Cybersecurity Fundamentals",
      description:
        "Network security, ethical hacking, and security best practices",
      students: 167,
      completionRate: 62,
      status: "Active",
      created: "2024-02-10",
      duration: "6 months",
      modules: 11,
      difficulty: "Intermediate",
      mentor: "Dr. Sarah Khan",
      category: "Security",
    },
    {
      id: 7,
      name: "Cloud Computing",
      description: "AWS, Azure, and GCP fundamentals with hands-on projects",
      students: 142,
      completionRate: 55,
      status: "Active",
      created: "2024-03-15",
      duration: "5 months",
      modules: 9,
      difficulty: "Intermediate",
      mentor: "Eng. Ahmed Raza",
      category: "Cloud",
    },
    {
      id: 8,
      name: "Blockchain Development",
      description: "Smart contracts, DApps, and Web3 development",
      students: 98,
      completionRate: 48,
      status: "Draft",
      created: "2024-04-20",
      duration: "7 months",
      modules: 13,
      difficulty: "Advanced",
      mentor: "Prof. Hassan Ali",
      category: "Blockchain",
    },
  ];

  const stats = {
    total: roadmapsData.length,
    active: roadmapsData.filter((r) => r.status === "Active").length,
    students: roadmapsData.reduce((sum, r) => sum + r.students, 0),
    avgCompletion: Math.round(
      roadmapsData.reduce((sum, r) => sum + r.completionRate, 0) /
        roadmapsData.length,
    ),
  };

  const filteredRoadmaps = roadmapsData.filter((roadmap) => {
    const matchesFilter =
      selectedFilter === "all" ||
      roadmap.status.toLowerCase() === selectedFilter;
    const matchesSearch =
      roadmap.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      roadmap.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredRoadmaps.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRoadmaps = filteredRoadmaps.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRoadmapClick = (roadmap) => {
    setSelectedRoadmap(roadmap);
  };

  const closeModal = () => {
    setSelectedRoadmap(null);
  };

  return (
    <AdminLayout>
      <div className="roadmaps-container">
        {/* Header */}
        <div className="roadmaps-header">
          <div>
            <h2>Learning Roadmaps</h2>
            <p className="roadmaps-subtitle">
              Create and manage structured learning paths
            </p>
          </div>
          <button
            className="create-roadmap-btn"
            onClick={() => navigate("/admin/create-roadmap")}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create Roadmap
          </button>
        </div>

        {/* Stats Grid */}
        <div className="roadmap-stats-grid">
          <div className="roadmap-stat-card">
            <div className="stat-content">
              <div className="stat-icon total">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 3h7l1 3h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
                </svg>
              </div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Roadmaps</span>
            </div>
          </div>

          <div className="roadmap-stat-card">
            <div className="stat-content">
              <div className="stat-icon active">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <span className="stat-value">{stats.active}</span>
              <span className="stat-label">Active Roadmaps</span>
            </div>
          </div>

          <div className="roadmap-stat-card">
            <div className="stat-content">
              <div className="stat-icon students">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <span className="stat-value">
                {stats.students.toLocaleString()}
              </span>
              <span className="stat-label">Total Students</span>
            </div>
          </div>

          <div className="roadmap-stat-card">
            <div className="stat-content">
              <div className="stat-icon completion">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <span className="stat-value">{stats.avgCompletion}%</span>
              <span className="stat-label">Avg Completion</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="roadmap-controls">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${selectedFilter === "all" ? "active" : ""}`}
              onClick={() => {
                setSelectedFilter("all");
                setCurrentPage(1);
              }}
            >
              All Roadmaps
            </button>
            <button
              className={`filter-tab ${selectedFilter === "active" ? "active" : ""}`}
              onClick={() => {
                setSelectedFilter("active");
                setCurrentPage(1);
              }}
            >
              Active
            </button>
            <button
              className={`filter-tab ${selectedFilter === "draft" ? "active" : ""}`}
              onClick={() => {
                setSelectedFilter("draft");
                setCurrentPage(1);
              }}
            >
              Draft
            </button>
          </div>

          <div className="rm-right-controls">
            <div className="search-box">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search roadmaps..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="rm-view-toggle">
              <button
                className={`rm-view-btn ${viewMode === "table" ? "rm-view-active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="8" y1="6" x2="21" y2="6"></line>
                  <line x1="8" y1="12" x2="21" y2="12"></line>
                  <line x1="8" y1="18" x2="21" y2="18"></line>
                  <line x1="3" y1="6" x2="3.01" y2="6"></line>
                  <line x1="3" y1="12" x2="3.01" y2="12"></line>
                  <line x1="3" y1="18" x2="3.01" y2="18"></line>
                </svg>
              </button>
              <button
                className={`rm-view-btn ${viewMode === "grid" ? "rm-view-active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === "grid" && (
          <>
            <div className="roadmaps-grid">
              {currentRoadmaps.map((roadmap) => (
                <div
                  key={roadmap.id}
                  className="roadmap-card"
                  onClick={() => handleRoadmapClick(roadmap)}
                >
                  <div className="roadmap-card-header">
                    <h3>{roadmap.name}</h3>
                    <span
                      className={`status-badge ${roadmap.status.toLowerCase()}`}
                    >
                      {roadmap.status}
                    </span>
                  </div>

                  <p className="roadmap-description">{roadmap.description}</p>

                  <div className="roadmap-meta">
                    <div className="meta-item">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>{roadmap.duration}</span>
                    </div>
                    <div className="meta-item">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                      <span>{roadmap.modules} modules</span>
                    </div>
                    <div className="meta-item">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>{roadmap.students} students</span>
                    </div>
                  </div>

                  <div className="completion-section">
                    <div className="completion-header">
                      <span>Completion Rate</span>
                      <span className="completion-percent">
                        {roadmap.completionRate}%
                      </span>
                    </div>
                    <div className="completion-bar">
                      <div
                        className="completion-fill"
                        style={{ width: `${roadmap.completionRate}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="roadmap-card-footer">
                    <div className="mentor-info">
                      <div className="mentor-avatar">
                        {roadmap.mentor.charAt(0)}
                      </div>
                      <span>{roadmap.mentor}</span>
                    </div>
                    <span
                      className={`difficulty-badge ${roadmap.difficulty.toLowerCase()}`}
                    >
                      {roadmap.difficulty}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="rm-pagination">
                <div className="rm-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="rm-pagination-buttons">
                  <button
                    className="rm-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                  </button>
                  <button
                    className="rm-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Table View */}
        {viewMode === "table" && (
          <>
            <div className="rm-table-container">
              <table className="rm-table">
                <thead>
                  <tr>
                    {/* ── ID column: label has #, cells do not ── */}
                    <th># ID</th>
                    <th>Roadmap Name</th>
                    <th>Category</th>
                    <th>Mentor</th>
                    <th>Duration</th>
                    <th>Modules</th>
                    <th>Students</th>
                    <th>Completion</th>
                    <th>Difficulty</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRoadmaps.map((roadmap) => (
                    <tr
                      key={roadmap.id}
                      onClick={() => handleRoadmapClick(roadmap)}
                      className="rm-table-row"
                    >
                      {/* Plain number, no background, no border, no # */}
                      <td className="rm-table-id">{roadmap.id}</td>
                      <td className="rm-table-name">{roadmap.name}</td>
                      <td>{roadmap.category}</td>
                      <td>{roadmap.mentor}</td>
                      <td>{roadmap.duration}</td>
                      <td>{roadmap.modules}</td>
                      <td>{roadmap.students}</td>
                      <td>{roadmap.completionRate}%</td>
                      <td>
                        <span
                          className={`difficulty-badge ${roadmap.difficulty.toLowerCase()}`}
                        >
                          {roadmap.difficulty}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${roadmap.status.toLowerCase()}`}
                        >
                          {roadmap.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="rm-pagination">
                <div className="rm-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="rm-pagination-buttons">
                  <button
                    className="rm-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                  </button>
                  <button
                    className="rm-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {filteredRoadmaps.length === 0 && (
          <div className="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 3h7l1 3h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
            </svg>
            <h3>No roadmaps found</h3>
            <p>Try adjusting your filters or create a new roadmap</p>
          </div>
        )}

        {/* Roadmap Detail Modal */}
        {selectedRoadmap && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3>{selectedRoadmap.name}</h3>
                  <span className="modal-category">
                    {selectedRoadmap.category}
                  </span>
                </div>
                <button className="close-btn" onClick={closeModal}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <p className="roadmap-full-description">
                  {selectedRoadmap.description}
                </p>

                <div className="roadmap-details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">{selectedRoadmap.duration}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Modules</span>
                    <span className="detail-value">{selectedRoadmap.modules}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Difficulty</span>
                    <span className={`difficulty-badge ${selectedRoadmap.difficulty.toLowerCase()}`}>
                      {selectedRoadmap.difficulty}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Students</span>
                    <span className="detail-value">{selectedRoadmap.students}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Completion Rate</span>
                    <span className="detail-value">{selectedRoadmap.completionRate}%</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status</span>
                    <span className={`status-badge ${selectedRoadmap.status.toLowerCase()}`}>
                      {selectedRoadmap.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Mentor</span>
                    <span className="detail-value">{selectedRoadmap.mentor}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created</span>
                    <span className="detail-value">{selectedRoadmap.created}</span>
                  </div>
                </div>

                <div className="modules-preview">
                  <h4>Learning Path</h4>
                  <div className="module-list">
                    {[...Array(selectedRoadmap.modules)].map((_, index) => (
                      <div key={index} className="module-item">
                        <div className="module-number">{index + 1}</div>
                        <div className="module-info">
                          <span className="module-title">Module {index + 1}</span>
                          <span className="module-status">In Progress</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="modal-btn secondary">View Analytics</button>
                <button className="modal-btn secondary">Manage Students</button>
                <button className="modal-btn primary">Edit Roadmap</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Roadmaps;